import React, { useState } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { FormModal, StarRating, TextField } from '../common';
import { useTheme } from '../../hooks/useTheme';
import { API_URL } from '../../constants/config';
import { getToken } from '../../utils/storage';
import Text from '../common/Text';

interface FeedbackFormProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      Alert.alert('Error', 'Please enter your feedback message');
      return;
    }

    if (rating === 0) {
      Alert.alert('Error', 'Please provide a rating');
      return;
    }

    setLoading(true);
    try {
      const token = await getToken('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/feedback/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          subject: subject.trim() || undefined,
          message: message.trim(),
          category: 'feedback',
          rating: rating,
        }),
      });

      if (response.ok) {
        // Reset form
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
        setRating(0);

        // Close modal and show toast
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errBody = await response.json().catch(() => ({}));
        const detail = Array.isArray(errBody.detail)
          ? errBody.detail
              .map(
                (e: { msg?: string; loc?: string[] }) =>
                  e.msg || e.loc?.join('.')
              )
              .join(', ')
          : errBody.detail || 'Failed to submit feedback';
        throw new Error(
          typeof detail === 'string' ? detail : 'Failed to submit feedback'
        );
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to submit feedback. Please try again.';
      Alert.alert('Error', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      visible={visible}
      onClose={onClose}
      title="Share Your Feedback"
      onSubmit={handleSubmit}
      submitLabel="Submit Feedback"
      cancelLabel="Cancel"
      loading={loading}
    >
      {/* Star Rating */}
      <View style={styles.ratingSection}>
        <Text style={[styles.modalLabel, { color: colors.text }]}>
          How would you rate your experience? *
        </Text>
        <StarRating
          rating={rating}
          onRatingChange={setRating}
          maxStars={5}
          size={36}
          showLabel={true}
          style={styles.starRating}
        />
      </View>

      <TextField
        label="Name (Optional)"
        value={name}
        onChangeText={setName}
        placeholder="Enter your name"
      />

      <TextField
        label="Email (Optional)"
        value={email}
        onChangeText={setEmail}
        placeholder="Enter your email"
        keyboardType="email-address"
        autoCapitalize="none"
      />

      <TextField
        label="Subject (Optional)"
        value={subject}
        onChangeText={setSubject}
        placeholder="Brief description of your feedback"
      />

      <TextField
        label="Your Feedback *"
        value={message}
        onChangeText={setMessage}
        placeholder="Tell us what you think about ColourSense..."
        multiline
        numberOfLines={5}
      />
    </FormModal>
  );
};

const styles = StyleSheet.create({
  ratingSection: {
    marginBottom: 8,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  starRating: {
    marginTop: 4,
  },
});

export default FeedbackForm;
